import React, { createContext, useState, useEffect, useContext } from 'react';
import { createUser as apiCreateUser, signInWithEmail as apiSignIn, signInWithGoogle as apiGoogleSignIn, resetPassword as apiResetPassword, signOut as apiSignOut } from '../services/api';

// Create the auth context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Enhanced function to properly set user and persist in localStorage
  const persistUser = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  // Check localStorage on mount to restore user session
  useEffect(() => {
    const checkAuth = () => {
      const storedUser = localStorage.getItem('currentUser');
      const authToken = localStorage.getItem('authToken');
      
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setCurrentUser(user);
          console.log('User session restored from localStorage:', user);
          
          // Even if token is missing, keep the user logged in for the session
          // This prevents unnecessary redirects on refresh
          if (!authToken) {
            console.log('Auth token missing but continuing session to prevent redirect');
          }
        } catch (e) {
          console.error('Error parsing stored user:', e);
          // Clear corrupted data
          localStorage.removeItem('currentUser');
          localStorage.removeItem('authToken');
        }
      }
      
      setLoading(false);
    };

    checkAuth();
    
    // Add event listener for storage changes (useful for multi-tab scenarios)
    window.addEventListener('storage', (event) => {
      if (event.key === 'authToken' && !event.newValue) {
        // Token was removed in another tab
        setCurrentUser(null);
      } else if (event.key === 'currentUser') {
        checkAuth();
      }
    });
  }, []);

  // Create a new user with the backend API
  const createUser = async (userData) => {
    setLoading(true);
    try {
      console.log("Attempting to register user:", userData);
      const response = await apiCreateUser(userData);
      
      console.log("Registration response:", response);
      
      // Handle successful registration
      const user = {
        id: response.user_id || 'temp-id',
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        loggedIn: true
      };
      
      // Store in state and localStorage
      persistUser(user);
      setError(null);
      
      return user;
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email
  const signInWithEmail = async (email, password) => {
    setLoading(true);
    try {
      console.log("Attempting to sign in user:", email);
      const response = await apiSignIn(email, password);
      
      console.log("Login response:", response);
      
      // Handle successful login
      const user = {
        id: response.user_id || 'temp-id',
        email: email,
        loggedIn: true
      };
      
      // Store in state and localStorage
      persistUser(user);
      setError(null);
      
      return user;
    } catch (err) {
      console.error('Error signing in with email:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    setLoading(true);
    try {
      await apiSignOut();
      
      // Clear state and localStorage completely
      setCurrentUser(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('guestUserId');
      localStorage.removeItem('user');
      localStorage.removeItem('mock');
      
      setError(null);
      
      // Show success message before redirecting
      const showSuccess = (message) => {
        const successPopup = document.createElement('div');
        successPopup.style.position = 'fixed';
        successPopup.style.top = '20px';
        successPopup.style.right = '20px';
        successPopup.style.background = 'linear-gradient(to right bottom, #FFF1F9, #E4EAFF)';
        successPopup.style.border = '1.5px solid transparent';
        successPopup.style.backgroundImage = 'linear-gradient(to right bottom, #FFF1F9, #E4EAFF), linear-gradient(to bottom, #0782FE, #E80694)';
        successPopup.style.backgroundOrigin = 'border-box';
        successPopup.style.backgroundClip = 'padding-box, border-box';
        successPopup.style.color = 'black';
        successPopup.style.padding = '16px 24px';
        successPopup.style.borderRadius = '8px';
        successPopup.style.display = 'flex';
        successPopup.style.alignItems = 'center';
        successPopup.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        successPopup.style.zIndex = '10000';
        successPopup.style.animation = 'slideInRight 0.3s ease-out';
        
        const successIcon = document.createElement('div');
        successIcon.style.backgroundColor = 'white';
        successIcon.style.color = '#4CAF50';
        successIcon.style.width = '24px';
        successIcon.style.height = '24px';
        successIcon.style.borderRadius = '50%';
        successIcon.style.display = 'flex';
        successIcon.style.alignItems = 'center';
        successIcon.style.justifyContent = 'center';
        successIcon.style.marginRight = '12px';
        successIcon.style.fontWeight = 'bold';
        successIcon.textContent = '✓';
        
        const successText = document.createElement('div');
        successText.style.fontFamily = "'Space Grotesk', sans-serif";
        successText.style.fontWeight = '500';
        successText.style.color = 'black';
        successText.textContent = message;
        
        successPopup.appendChild(successIcon);
        successPopup.appendChild(successText);
        document.body.appendChild(successPopup);
        
        // Add the animation to the document
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `;
        document.head.appendChild(style);
        
        // Remove popup after 3 seconds
        setTimeout(() => {
          document.body.removeChild(successPopup);
          document.head.removeChild(style);
          window.location.href = '/';
        }, 1500);
      };
      
      showSuccess("Successfully signed out!");
      
      return true;
    } catch (err) {
      console.error('Error signing out:', err);
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('guestUserId');
      localStorage.removeItem('user');
      localStorage.removeItem('mock');
      
      setCurrentUser(null); 
      setError(err.message);
      window.location.href = '/';
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiResetPassword(email);
      return response;
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      console.log("Attempting to sign in with Google");
      const response = await apiGoogleSignIn();
      
      console.log("Google sign-in response:", response);
      
      if (response && response.error) {
        console.error("Google sign-in failed:", response.message);
        return false;
      }
      
      if (response && response.user) {
        const user = {
          id: response.user.id || 'google-user',
          email: response.user.email,
          firstName: response.user.first_name || response.user.firstName,
          lastName: response.user.last_name || response.user.lastName,
          loggedIn: true
        };
        
        // Store in state and localStorage
        persistUser(user);
        return true;
      } else {
        console.error("Invalid response from Google sign-in");
        return false;
      }
    } catch (error) {
      console.error("Error in Google sign-in:", error);
      return false;
    } finally {
     // setLoading(false);
    }
  };

  // Value to be provided by the context
  const value = {
    currentUser,
    loading,
    error,
    createUser,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 